import {useDispatch, useSelector} from 'Provider';
import React, {useCallback} from 'react';

import OfferCard from 'components/Offers/OfferCard';
import {useFavoriteOffer} from 'hooks/useFavoriteOffer';
import {usePropertyList} from 'hooks/usePropertyList';
import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';
import {Offer} from 'models/Offer';
import {OffersActions} from 'store/offers/offers.reducer';

import {
  focusedCardIdSelector,
  offersSaveOfferPrefLoadingSelector,
  selectIsListView,
  selectedGroupingSelector,
  selectedOfferIdSelector,
} from '../../store/offers/offers.selectors';
import {OFFERS_GROUPING, ParamsList} from 'components/Offers/OfferGroup';
import {RouteProp, useRoute} from '@react-navigation/native';

interface ConnectedOfferCardProps {
  offer: Offer;
  testID?: string;
  cardIndex?: number;
  setSelectedGrouping: () => void;
  groupingKey?: OFFERS_GROUPING;
}

const ConnectedOfferCard = ({
  offer,
  testID,
  cardIndex,
  setSelectedGrouping,
  groupingKey,
}: ConnectedOfferCardProps) => {
  const dispatch = useDispatch();
  const {offerProperties, notFound} = usePropertyList(offer.propertyList);
  const offerPrefLoadingArray = useSelector(offersSaveOfferPrefLoadingSelector);
  const isOfferPrefLoading = offerPrefLoadingArray?.includes(offer.id);
  const onFavoritePress = useFavoriteOffer(offer);
  const focusedCardId = useSelector(focusedCardIdSelector);
  const isListView = useSelector(selectIsListView);
  const selectedOfferId = useSelector(selectedOfferIdSelector);
  const selectedGrouping = useSelector(selectedGroupingSelector);

  const dashedCode = notFound.find(code => code === '---');
  const properties = dashedCode ? [] : [...offerProperties];
  const additional = dashedCode ? [] : notFound.filter(code => code !== '---');
  const route = useRoute<RouteProp<ParamsList, 'OffersGrouping'>>();
  const isInCurrentGrouping =
    !selectedGrouping || route.params?.group
      ? true
      : selectedGrouping?.key === groupingKey;
  const {trackAdobeEvent} = useTrackEvent();

  const handleSelectOfferId = useCallback(() => {
    trackAdobeEvent({
      event: AdobeEvent.click,
      click: {name: 'rewards offer details'},
    });
    setSelectedGrouping();
    dispatch(OffersActions.setSelectedOfferId(offer.id));
    dispatch(OffersActions.setFocusedCardId(offer.id));
  }, [dispatch, offer.id, setSelectedGrouping, trackAdobeEvent]);

  const clearFocusedCardId = useCallback(() => {
    dispatch(OffersActions.setFocusedCardId(undefined));
  }, [dispatch]);

  return (
    <OfferCard
      cardIndex={cardIndex}
      offer={offer}
      properties={properties}
      onNextPress={handleSelectOfferId}
      onFavoritePress={onFavoritePress}
      testID={testID}
      additionalPropertyCodes={additional}
      isOfferPrefLoading={isOfferPrefLoading}
      clearFocusedCardId={clearFocusedCardId}
      focusedCardId={focusedCardId}
      selectedOfferId={selectedOfferId}
      isListView={isListView}
      isInCurrentGrouping={isInCurrentGrouping}
    />
  );
};

export default ConnectedOfferCard;
